import { createContext, useContext, useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { MedicalPaOrder } from "../data-model/medicalPaOrdersModel";
import { RootState } from "../redux/store/store";

/**
 * Props interface for the Initial Data of Single Medical Order context.
 * Contains the initial snapshot of a medical PA order and derived values
 * captured at the time of first load.
 *
 * @property {MedicalPaOrder} [singleMedicalPaOrder] - The initial medical PA order data snapshot
 * @property {boolean} initialDataIsLoaded - Flag indicating if the initial data has been loaded
 * @property {string[]} [oldPrimaryIcdCodes] - Array of primary ICD codes from the initial load
 * @property {number} [countOfPrimaryIcdCodes] - Count of primary ICD codes from the initial load
 * @property {string} [oldAuthStatus] - The auth status from the initial load
 * @property {string} [currentOrderId] - The order ID for which data is currently captured
 */
interface InitialDataOfSingleMedicalOrderProps {
  singleMedicalPaOrder?: MedicalPaOrder;
  initialDataIsLoaded: boolean;
  oldPrimaryIcdCodes?: string[];
  countOfPrimaryIcdCodes?: number;
  oldAuthStatus?: string;
  currentOrderId?: string;
}

/**
 * Context for storing the initial state of a single medical PA order.
 * This context captures the order data on first load and preserves it,
 * allowing comparison with subsequent Redux state changes.
 */
const InitialDataOfSingleMedicalOrderContext =
  createContext<InitialDataOfSingleMedicalOrderProps>({
    singleMedicalPaOrder: undefined,
    initialDataIsLoaded: false,
  });

/**
 * Custom hook to access the initial data of a single medical order.
 * Provides access to the initial snapshot of the order data and derived values.
 *
 * @returns {InitialDataOfSingleMedicalOrderProps} The initial order data context value
 *
 * @example
 * const { singleMedicalPaOrder, oldAuthStatus, initialDataIsLoaded } = useInitialDataOfSingleMedicalOrder();
 */
export const useInitialDataOfSingleMedicalOrder = () => {
  return useContext(InitialDataOfSingleMedicalOrderContext);
};

/**
 * Provider component that captures and stores the initial state of a single medical PA order.
 *
 * This provider:
 * - Subscribes to the Redux store for the single medical PA order
 * - Captures the order data only on the first load (when `initialDataIsLoaded` is false)
 * - Extracts and stores derived values (ICD codes, auth status) from the initial data
 * - Prevents subsequent Redux updates from overwriting the initial snapshot
 * - **Automatically resets** when the `orderId` changes (e.g., navigating to a different order)
 *
 * @param {Object} props - Component props
 * @param {React.ReactNode} props.children - Child components to wrap with the provider
 * @returns {JSX.Element} Provider component wrapping children with initial order data context
 *
 * @example
 * <InitialDataOfSingleMedicalOrderProvider>
 *   <SingleOrderLayout />
 * </InitialDataOfSingleMedicalOrderProvider>
 */
export const InitialDataOfSingleMedicalOrderProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const { singleMedicalPaOrder: reduxSingleMedicalPaOrder } = useSelector(
    (state: RootState) => state.singleMedicalPaOrder,
  );

  const [singleMedicalPaOrder, setSingleMedicalPaOrder] = useState<
    MedicalPaOrder | undefined
  >(undefined);
  const [initialDataIsLoaded, setInitialDataIsLoaded] = useState(false);
  const [oldPrimaryIcdCodes, setOldPrimaryIcdCodes] = useState<string[]>([]);
  const [countOfPrimaryIcdCodes, setCountOfPrimaryIcdCodes] = useState(0);
  const [oldAuthStatus, setOldAuthStatus] = useState<string>("");
  const [currentOrderId, setCurrentOrderId] = useState<string | undefined>(
    undefined,
  );

  // Track the previous order ID to detect changes
  const previousOrderIdRef = useRef<string | undefined>(undefined);

  /**
   * Resets all context state to initial values.
   * Called when order ID changes to ensure fresh data capture.
   */
  const resetContextState = () => {
    setSingleMedicalPaOrder(undefined);
    setInitialDataIsLoaded(false);
    setOldPrimaryIcdCodes([]);
    setCountOfPrimaryIcdCodes(0);
    setOldAuthStatus("");
    setCurrentOrderId(undefined);
  };

  /**
   * Extracts primary ICD codes from the single medical PA order.
   * @returns {string[]} Array of ICD code strings
   */
  const getOldPrimaryIcdCodes = () => {
    const primaryIcdCodes = singleMedicalPaOrder?.primary_diagnosis_details;
    if (Array.isArray(primaryIcdCodes)) {
      return primaryIcdCodes.map((detail) => detail.icd_code ?? "");
    }
    return [];
  };

  /**
   * Gets the count of primary ICD codes from the single medical PA order.
   * @returns {number} The count of primary diagnosis details
   */
  const getCountOfPrimaryIcdCodes = () => {
    return singleMedicalPaOrder?.primary_diagnosis_details?.length ?? 0;
  };

  /**
   * Gets the auth status from the single medical PA order.
   * @returns {string} The auth status string or empty string if not available
   */
  const getOldAuthStatus = () => {
    return singleMedicalPaOrder?.auth_on_file?.auth_status ?? "";
  };

  // Effect to derive and store values from the initial order data
  useEffect(() => {
    if (singleMedicalPaOrder) {
      setOldPrimaryIcdCodes(getOldPrimaryIcdCodes());
      setCountOfPrimaryIcdCodes(getCountOfPrimaryIcdCodes());
      setOldAuthStatus(getOldAuthStatus());
    }
  }, [singleMedicalPaOrder]);

  // Effect to detect order ID changes and reset context state
  useEffect(() => {
    const newOrderId = reduxSingleMedicalPaOrder?.order_id;

    // If order ID has changed (and we had a previous order), reset the context
    if (
      previousOrderIdRef.current !== undefined &&
      newOrderId !== undefined &&
      previousOrderIdRef.current !== newOrderId
    ) {
      resetContextState();
    }

    // Update the previous order ID ref
    previousOrderIdRef.current = newOrderId;
  }, [reduxSingleMedicalPaOrder?.order_id]);

  // Effect to capture initial order data only once on first load
  useEffect(() => {
    if (!initialDataIsLoaded && reduxSingleMedicalPaOrder) {
      setSingleMedicalPaOrder(reduxSingleMedicalPaOrder);
      setCurrentOrderId(reduxSingleMedicalPaOrder.order_id);
      setInitialDataIsLoaded(true);
    }
  }, [reduxSingleMedicalPaOrder, initialDataIsLoaded]);

  return (
    <InitialDataOfSingleMedicalOrderContext.Provider
      value={{
        singleMedicalPaOrder,
        initialDataIsLoaded,
        oldPrimaryIcdCodes,
        countOfPrimaryIcdCodes,
        oldAuthStatus,
        currentOrderId,
      }}
    >
      {children}
    </InitialDataOfSingleMedicalOrderContext.Provider>
  );
};
